import { RoundButton } from "@/components/buttons/Buttons";
import CardContainer from "@/components/cards/CardContainer";
import Container, { Column, Row } from "@/components/container/Container";
import { TextBodyExtraExtraSmall, TextBodyMedium, TextHeadlineMedium, TextTitleSmall2, TextTableHeader } from "@/components/text/Texts";
import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { MdStar } from "react-icons/md";
import { mapActivityToReturnTrip, mapActivityToReturnSendBooking, TripType } from "../engine/ConciergeCardViewModel";
import { CardContext } from "../context/CardContext";
import clsx from "clsx";
import { formatCurrency, GenericResponse, showTypedModal2 } from "@/common/common_utils";
import ReportLostItemDialog from "./ReportLostItemDialog";
import { ConciergeLiveMap } from "../map/ConciergeLiveMap";
import { axiosInstance } from "@/services/axios.service";

import { ENDPOINTS } from "@/constants/endpoints";
import { getVehicleTypeInfo } from "@/modules/vehicle/engine/VehicleType";
import { GoogleMapsRoutingResponse } from "@/modules/map/engine/GoogleTypes";
import { X4bActivityStatus } from "@/services/signalr.service";
import { BookingType } from "@/modules/vehicle/engine/ActivityTypes";
import { ActivityHelper } from "@/modules/vehicle/engine/X4bActivityDto";
import { fetchActivityRouteData } from "../map/useInitialRoute";
import { useMutation } from "@tanstack/react-query";
import { showNotification } from "@/modules/notification/engine/notification.library";
import { useNavigate } from "react-router-dom";
import { routeWithId } from "@/constants/routes";
import { useAuthenticationStore } from "@/store";
import { X4bUserRoleEnum } from "@/constants/roles";

const ConciergeCardInfo = () => {
	const { selectedCard } = useContext(CardContext);
	if (selectedCard === null) throw new Error('selectedCard is null');

	const hasPassengers = selectedCard.additionalPassengers.length > 0;
	const isRequesting = ActivityHelper.isRequesting(selectedCard.act);
	const isCompleted = ActivityHelper.isCompleted(selectedCard.act);
	const isManagerOrUser = useAuthenticationStore((state) => state.hasRoles)([X4bUserRoleEnum.User, X4bUserRoleEnum.Manager]);

	const navigate = useNavigate();

	// State to store the routing data for displaying markers
	const [routingData, setRoutingData] = useState<GoogleMapsRoutingResponse | null>(null);

	// Setting trip id in the LiveMap component is tricky because the component itself is momoized. So, we can pass trip id in normal way and need to use bind setter and use refs.
	const tripIdSetterRef = useRef<(tripId: string) => void>(undefined);
	const bindTripIdSetter = useCallback((setter: (tripId: string) => void) => {
		tripIdSetterRef.current = setter;
	}, []);
	// push
	const orderUuidSetterRef = useRef<(orderUuid: string) => void>(undefined);
	const bindOrderUuidSetter = useCallback((setter: (tripId: string) => void) => {
		orderUuidSetterRef.current = setter;
	}, []);

	const rebookTrip = useMutation({
		mutationFn: async () => {
			if (!selectedCard.id) throw new Error('Missing order id');
			const placeOrder = await axiosInstance.post<any>(ENDPOINTS['/api/X4bActivity/RebookOrder'], {
				Id: selectedCard.id
			});

			return { placeOrder };
		},
		onSuccess: () => {
			showNotification('Booking has been requested');
			navigate('/concierge/statuses/0');
		},

	});

	useEffect(() => {
		if (!selectedCard.xpressId) throw new Error("Ivalid order uuid");

		fetchActivityRouteData(
			selectedCard.act,
			selectedCard.id,
			selectedCard.xpressId,
			setRoutingData,
			orderUuidSetterRef,
			tripIdSetterRef,
			true
		);
	}, [selectedCard]);

	let driverInfo;
	if (ActivityHelper.isAwaitingPickup(selectedCard.act)
		|| ActivityHelper.isInProgress(selectedCard.act)
		|| ActivityHelper.isCompleted(selectedCard.act)
	) {
		driverInfo = (
			<Row className="ml-6 items-center">
				<Column>
					<Row>
						<TextTableHeader
							text={selectedCard.driverName}
							className="whitespace-nowrap truncate text-black"
						/>
						<MdStar size={24} className="ml-[10px] mr-2" />
						<TextTableHeader
							text={selectedCard.driverRating?.toFixed(1)}
							className="whitespace-nowrap truncate text-black"
						/>
					</Row>
					<TextTitleSmall2
						text={`${selectedCard.vehicle.plate} · ${selectedCard.vehicle.name}`}
						className="whitespace-nowrap truncate text-neutral60"
					/>
				</Column>
				<Container className="relative ml-auto my-1">
					<img
						src={getVehicleTypeInfo(selectedCard.vehicle.type)?.imageLargePath}
						alt="Driver vehicle" />
					<img
						src="/images/concierge/user-avatar.png" alt="Driver avatar"
						className="absolute left-0 top-1/2 -translate-y-1/2"
					/>
				</Container>
			</Row>
		);
	}

	let buttons;
	if (ActivityHelper.isInProgress(selectedCard.act)) {
		buttons = (
			<Row className="gap-4 justify-end tablet:flex-wrap">
				<RoundButton
					id='concierge-contact-support'
					label="Contact Support"
					onClick={() => { }}
					pt={{
						root: { className: 'bg-primary99 self-end tablet:shrink-0 tablet:!px-3' },
						label: { className: 'text-secondary60 leading-[24px] tracking-[0.1px] tablet:whitespace-nowrap' },
					}}
				/>
				{/* TODO: remove after testing */}
				<RoundButton
					id='concierge-report-lost-item'
					label="Report lost item"
					onClick={async () => {
						await showTypedModal2(ReportLostItemDialog, {});
					}}
					pt={{
						root: { className: 'bg-primary99 self-end tablet:shrink-0 tablet:!px-3' },
						label: { className: 'text-secondary60 leading-[24px] tracking-[0.1px] tablet:whitespace-nowrap' },
					}}
				/>
			</Row>
		);
	} else if (ActivityHelper.isCompleted(selectedCard.act)) {
		buttons = isManagerOrUser ? (

			<Row className="gap-4 justify-end tablet:flex-wrap">
				{selectedCard.type === TripType.OneWay && (
					<RoundButton
						id='concierge-book-return'
						label="Book return"
						onClick={(e) => {
							e.stopPropagation();

							// Send/delivery orders must reopen in the delivery flow, not the ride flow.
							if (ActivityHelper.isDelivery(selectedCard.act)) {
								const returnSendBooking = mapActivityToReturnSendBooking(selectedCard.act);
								navigate(routeWithId('/concierge/delivery/:id', 0), { state: { returnSendBooking } });
								return;
							}

							const returnTrip = mapActivityToReturnTrip(selectedCard.act);
							navigate(routeWithId('/concierge/rides/:id', 0), { state: { returnTrip } });
						}}
						pt={{
							root: { className: 'bg-primary99 self-end tablet:shrink-0 tablet:!px-3' },
							label: { className: 'text-secondary60 leading-[24px] tracking-[0.1px] tablet:whitespace-nowrap' },
						}}
					/>
				)}
				<RoundButton
					id='concierge-re-book-trip'
					label="Re-book trip"
					onClick={() => {
						rebookTrip.mutate();
					}}
					disabled={rebookTrip.isPending}
					loading={rebookTrip.isPending}
					pt={{
						root: { className: 'bg-primary99 self-end tablet:shrink-0 tablet:!px-3' },
						label: { className: 'text-secondary60 leading-[24px] tracking-[0.1px] tablet:whitespace-nowrap' },
					}}
				/>
				<RoundButton
					id='concierge-report-lost-item'
					label="Report lost item"
					onClick={async () => {
						await showTypedModal2(ReportLostItemDialog, {});
					}}
					pt={{
						root: { className: 'bg-primary99 self-end tablet:shrink-0 tablet:!px-3' },
						label: { className: 'text-secondary60 leading-[24px] tracking-[0.1px] tablet:whitespace-nowrap' },
					}}
				/>
			</Row>
		) : undefined;
	} else if (ActivityHelper.isCanceled(selectedCard.act)) {
		buttons = (
			<RoundButton
				id='concierge-re-book-trip'
				label="Re-book trip"
				onClick={() => {
					rebookTrip.mutate();
				}}
				disabled={rebookTrip.isPending}
				loading={rebookTrip.isPending}
				pt={{
					root: { className: 'bg-primary99 self-end' },
					label: { className: 'text-secondary60 leading-[24px] tracking-[0.1px]' },
				}}
			/>
		);
	} else if (false) {
		buttons = (
			<Row className="gap-4 justify-end">
				{/* TODO[Phase2] */}
				<RoundButton
					id='concierge-cancel-booking'
					label="Cancel Booking"
					onClick={() => { }}
					pt={{
						root: { className: 'bg-error99 self-end' },
						label: { className: 'text-error50 leading-[24px] tracking-[0.1px]' },
					}}
				/>
				<RoundButton
					id='concierge-report-lost-item'
					label="Report lost item"
					onClick={() => { }}
					pt={{
						root: { className: 'bg-primary99 self-end' },
						label: { className: 'text-secondary60 leading-[24px] tracking-[0.1px]' },
					}}
				/>
			</Row>
		);
	}

	return (
		<>


			<CardContainer rootClassName="rounded-xl p-[16px]">
				<Column className="gap-5">
					{/* Name, Phone */}
					<Column className="gap-2">
						<TextHeadlineMedium
							text={selectedCard.passenger.name}
							className="whitespace-nowrap truncate text-primary50"
						/>
						<TextBodyMedium
							text={selectedCard.passenger.phone}
							className="whitespace-nowrap truncate text-primary30"
						/>
					</Column>
					{/* Trip Info */}
					<Column className="ml-6 gap-3">
						{/* Pickup, Destination */}
						<Row className="gap-1">
							<img
								src={`/images/concierge/pickup-${selectedCard.type === TripType.OneWay ? 'oneway' : 'roundtrip'}.svg`}
								alt="Trip Type"
							/>
							<Column className="justify-between w-[calc(100%-(20px+4px))]"> {/* image size + gap */}
								<Column>
									<TextBodyMedium
										text={selectedCard.pickup.name}
										className="whitespace-nowrap truncate text-primary50"
									/>
									<TextBodyExtraExtraSmall
										text={selectedCard.pickup.address}
										className="whitespace-nowrap truncate text-neutral60"
									/>
								</Column>
								<Column>
									<TextBodyMedium
										text={selectedCard.destination.name}
										className="whitespace-nowrap truncate text-primary50"
									/>
									<TextBodyExtraExtraSmall
										text={selectedCard.destination.address}
										className="whitespace-nowrap truncate text-neutral60"
									/>
								</Column>
							</Column>
						</Row>
						{/* Additional Passengers */}
						{hasPassengers && (
							<Column>
								<TextTableHeader
									text="Additional Passengers"
									className="whitespace-nowrap truncate text-black"
								/>
								<Row className="gap-x-[10px] flex-wrap">
									{selectedCard.additionalPassengers.map((passenger, index) => (
										<TextTitleSmall2
											key={index}
											text={`${passenger.name} (${passenger.phone}),`}
											className="whitespace-nowrap truncate text-neutral40"
										/>
									))}
								</Row>
							</Column>
						)}
					</Column>
					{/* Trip Info */}
					<Column className="ml-6 gap-6">
						{/* Booking Amount */}
						<Column>
							<TextTableHeader
								text={formatCurrency(ActivityHelper.isCanceled(selectedCard.act) ? 0 : selectedCard.cost)}
								className="whitespace-nowrap truncate text-black"
							/>
							<TextTitleSmall2
								text={isCompleted ? `${selectedCard.duration} mins ride` : 'Booking Amount'}
								className="whitespace-nowrap truncate text-neutral60"
							/>
						</Column>
						{/* Requested by, Status */}
						<Row>
							<Row className={clsx('gap-2', { 'w-1/2': isRequesting })}>
								<TextTitleSmall2
									text="Requested by"
									className="whitespace-nowrap text-neutral60"
								/>
								<TextTableHeader
									text={
										selectedCard.act.CreatorName ?? ''}
									className="whitespace-nowrap truncate text-black"
								/>
							</Row>
							{isRequesting && (
								<TextTableHeader
									text="Finding a driver..."
									className="w-1/2 whitespace-nowrap truncate text-black"
								/>
							)}
						</Row>
						{/* Memo, Note */}
						<Row>
							<Column className="w-1/2">
								<TextTableHeader
									text="Internal Memo"
									className="whitespace-nowrap truncate text-black w-full"
								/>
								<TextTitleSmall2
									text={selectedCard.infernalMemo || ''}
									className="whitespace-nowrap truncate text-neutral60"
								/>
							</Column>
							<Column className="w-1/2">
								<TextTableHeader
									text="Note to Driver"
									className="whitespace-nowrap truncate text-black w-full"
								/>
								<TextTitleSmall2
									text={selectedCard.noteToDriver || ''}
									className="whitespace-nowrap truncate text-neutral60"
								/>
							</Column>
						</Row>
					</Column>
					{/* Map */}
					{/* <img src="/images/map.png" alt="map placeholder" /> */}
					<Container className="overflow-hidden h-[300px] rounded-xl">
						<ConciergeLiveMap
							// When key changes the component is treated as different and fully replaced. We need that because otherwise will need to manually replace/clean state of map for different orders. Which is very bad idea.
							key={selectedCard.xpressId}
							bindTripIdSetter={bindTripIdSetter}
							bindOrderUuidSetter={bindOrderUuidSetter}
							pickupLocationName={selectedCard.pickup.name || selectedCard.pickup.address}
							dropOffLocationName={selectedCard.destination.name || selectedCard.destination.address}
							mapDivClassName='min-h-[300px]'// should match height of outer div, otherwise we get map autofit broken and map controls missing
							initialRoute={routingData}
							activity={selectedCard.act}
							showCompletedRoute={true}
						/>
					</Container>
					{/* Driver Info */}
					{driverInfo && driverInfo}
					{/* Buttons */}
					{buttons && buttons}
				</Column>
			</CardContainer>
		</>
	);
};

export default ConciergeCardInfo;
